import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Col, Input, Modal, Row, Spin, Steps, Switch, Tooltip, Typography } from 'antd';

import { useCreateConnect } from 'v2/hooks/useCreateConnect';
import { useListAutoresponders } from 'v2/hooks/useListAutoresponders';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { run } from 'validations/ruleRunner';

import { AUTORESPONDERS_WITH_CREATION_DISABLED, EStep } from './constants';
import Validations from './validations';
import { ConnectPlatform } from '../../constants';
import { useConnectState } from '../../contexts/ConnectStateContext';

const { RD_STATION, ACTIVE_CAMPAIGN, LEAD_LOVERS, BLING } = ConnectPlatform;

const NewConnect: React.FC = () => {
  const {
    newConnect,
    step,
    modalNewConnect: modal,
    nextStep: handleNext,
    previousStep: handleBack,
    setStep,
    setNewConnect,
    closeNewConnectModal
  } = useConnectState();
  const [validationErrors, setValidationErrors] = useState<any[]>([]);

  const { autoresponders } = useListAutoresponders();
  const createConnectMutation = useCreateConnect();

  const errorFor = useCallback((field: any) => validationErrors[field] || '', [validationErrors]);
  const isValid = useCallback((field: any) => !!validationErrors[field], [validationErrors]);

  const handleChange = useCallback(
    (field: any, value: any) => {
      const updated = { ...newConnect, [field]: value };
      setNewConnect(updated);
    },
    [newConnect, setNewConnect]
  );

  const isValidToNextStep = useCallback(() => {
    const hasAutoresponderParams = Array.isArray(newConnect.autoresponder?.parameters?.parametros);
    const autoresponderParams: any[] = hasAutoresponderParams ? newConnect.autoresponder?.parameters?.parametros : [];

    switch (step) {
      case EStep.PLATFORM:
        return !!newConnect.description;
      case EStep.ACCOUNT:
        return !!newConnect.autoresponder;

      case EStep.CONNECT: {
        let valid = true;
        autoresponderParams.forEach((param: any) => {
          valid = newConnect[param.nome] ? valid : false;
        });
        return valid;
      }

      default:
        return false;
    }
  }, [newConnect, step]);

  useEffect(() => {
    if (newConnect) {
      setValidationErrors(run(newConnect, Validations));
    }
  }, [newConnect]);

  const closeModal = useCallback(() => {
    closeNewConnectModal();
  }, [closeNewConnectModal]);

  const handleSaveNewConnect = useCallback(async () => {
    try {
      setStep(EStep.LOADING);
      const connectData = { ...newConnect };
      connectData.autoresponder_id = connectData.autoresponder.id;
      const fields: Record<string, any> = {};

      if (
        connectData.autoresponder &&
        connectData.autoresponder.parameters &&
        Array.isArray(connectData.autoresponder.parameters.parametros)
      ) {
        connectData.autoresponder.parameters.parametros.forEach((param: any) => {
          fields[param.nome] = connectData[param.nome];
          delete connectData[param.nome];
        });
      }

      connectData.fields = fields;
      delete connectData.autoresponder;
      await createConnectMutation.mutateAsync(connectData);
      setStep(EStep.DONE);
    } catch (error) {
      toast.error(error);
      setStep(EStep.CONNECT);
    }
  }, [newConnect, createConnectMutation, setStep]);

  const redirectUrl = useCallback((url: any) => {
    window.location.href = url;
  }, []);

  const paramType = useCallback(
    (param: any): any => {
      localStorage.setItem('connect-description', newConnect.description);

      if (param.tipo === 'field') {
        return (
          <div key={`parameter-${param.nome}`}>
            <label className='mb-1 block text-sm'>{param.label}</label>
            <Input onChange={(e: any) => handleChange(param.nome, e.target.value)} value={newConnect[param.nome]} />
          </div>
        );
      }

      if (param.tipo === 'botao') {
        const urlButton = param.botao_url;
        return (
          <Button type='primary' onClick={() => redirectUrl(`${urlButton}`)}>
            {param.botao_titulo}
          </Button>
        );
      }
    },
    [newConnect, handleChange, redirectUrl]
  );

  const steps = ['Descrição', 'Plataforma', 'Dados de configuração'];

  return (
    <Modal
      open={modal}
      onCancel={closeModal}
      width={640}
      title={
        <Typography.Title level={5} className='text-text-heading text-center'>
          Cadastro de Integração
        </Typography.Title>
      }
      footer={
        <div className='flex items-end justify-end gap-2'>
          <div>{step < EStep.DONE && step !== EStep.LOADING && <Button onClick={closeModal}>Cancelar</Button>}</div>
          <div className='flex justify-between gap-2'>
            {step !== EStep.PLATFORM && step < EStep.LOADING && (
              <Button onClick={handleBack}>
                <LeftOutlined />
                Voltar
              </Button>
            )}
            {step < EStep.LOADING && (
              <Button
                type='primary'
                onClick={step === steps.length - 1 ? handleSaveNewConnect : handleNext}
                disabled={!isValidToNextStep()}
              >
                {step === steps.length - 1 ? 'Salvar' : 'Avançar'}
                {step !== steps.length - 1 && <RightOutlined />}
              </Button>
            )}
            {step === EStep.DONE && (
              <Button type='primary' onClick={closeModal}>
                Fechar
              </Button>
            )}
          </div>
        </div>
      }
    >
      <Steps
        current={step}
        size='small'
        className='mb-4! sm:mb-6!'
        items={[{ title: 'Descrição' }, { title: 'Plataforma' }, { title: 'Dados de configuração' }]}
      />

      {step === EStep.PLATFORM && (
        <div>
          <div className='mb-4 flex items-center gap-4'>
            <span>{newConnect.active ? 'Ativo' : 'Inativo'}</span>
            <Switch checked={newConnect.active} onChange={(checked: boolean) => handleChange('active', checked)} />
          </div>
          <div>
            <label className='mb-1 block text-sm'>Descrição</label>
            <Input.TextArea
              rows={3}
              placeholder='Digite a descrição do connect'
              value={newConnect.description}
              onChange={(e: any) => handleChange('description', e.target.value)}
            />
            {isValid('description') && <span className='text-xs text-red-500'>{errorFor('description')}</span>}
          </div>
        </div>
      )}

      {step === EStep.ACCOUNT && (
        <Row gutter={[16, 16]}>
          {Array.isArray(autoresponders) &&
            autoresponders
              .filter(
                autoresponder =>
                  autoresponder.id !== RD_STATION && !AUTORESPONDERS_WITH_CREATION_DISABLED.includes(autoresponder.id)
              )
              .map((item: any) => (
                <Col key={item.id} span={6}>
                  <ListResponders item={item} newConnect={newConnect} handleChange={handleChange} />
                </Col>
              ))}
        </Row>
      )}

      {step === EStep.CONNECT && (
        <>
          <Row justify='center'>
            <Col span={12} className='text-center'>
              <div className='flex items-center justify-center p-4'>
                <span
                  className='block h-48 w-full bg-contain bg-center bg-no-repeat'
                  style={{
                    backgroundImage: `url(${newConnect.autoresponder?.image ?? null})`
                  }}
                />
              </div>
            </Col>
          </Row>
          <Row gutter={[16, 16]}>
            {Array.isArray(newConnect.autoresponder?.parameters?.parametros) &&
              newConnect.autoresponder.parameters.parametros.map((param: any) => (
                <Col span={16} key={param}>
                  {paramType(param)}
                </Col>
              ))}
          </Row>
        </>
      )}

      {step === EStep.LOADING && (
        <div className='text-center'>
          <Spin />
        </div>
      )}

      {step === EStep.DONE && (
        <div className='text-center'>
          <span> Connect salvo com sucesso. </span>
        </div>
      )}
    </Modal>
  );
};

const ListResponders = ({
  item,
  newConnect,
  handleChange
}: {
  item: any;
  newConnect: any;
  handleChange: (name: string, item: any) => void;
}) => {
  const isBeta = eduzzAccountsService.verifyUserHasBetaModule('new_connect');
  const betaProviders = [ACTIVE_CAMPAIGN, LEAD_LOVERS];
  const hasProviderInBeta = isBeta && betaProviders.includes(item.id);
  const isInactive = [BLING];
  const hasInactiveConnect = isInactive.includes(item.id);

  const isDisabled = hasProviderInBeta || hasInactiveConnect;
  const tooltipTitle = hasProviderInBeta
    ? 'Esta plataforma foi migrada para a nova versão do Connect Eduzz, acesse o novo connect para configurá-lo'
    : hasInactiveConnect
      ? 'Essa plataforma foi descontinuada, utilize nossa integração nativa com o Zapier'
      : '';

  const button = (
    <span className='w-1/3'>
      <button
        className='relative h-25 w-full cursor-pointer border-0 bg-transparent disabled:opacity-30 disabled:grayscale max-sm:h-25'
        onClick={() => handleChange('autoresponder', item)}
        disabled={isDisabled}
      >
        <span
          className='absolute inset-0 m-4 bg-size-[100%] bg-center bg-no-repeat'
          style={{ backgroundImage: `url(${item.image})` }}
        />
        <span
          className={
            newConnect.autoresponder?.name === item.name
              ? 'absolute inset-0 bg-black opacity-10 transition-opacity'
              : 'absolute inset-0'
          }
        />
        <span className='absolute inset-0 flex items-center justify-center text-white'></span>
      </button>
    </span>
  );

  if (isDisabled) {
    return (
      <Tooltip key={item.name} placement='top' title={tooltipTitle}>
        {button}
      </Tooltip>
    );
  }

  return button;
};

export default NewConnect;
